import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  IconButton, 
  Typography, 
  Button, 
  CircularProgress,
  LinearProgress,
  Slider,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardMedia,
  CardContent
} from '@mui/material';
import { styled } from '@mui/system';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import CropIcon from '@mui/icons-material/Crop';
import TuneIcon from '@mui/icons-material/Tune';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import BorderStyleIcon from '@mui/icons-material/BorderStyle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import WeekendIcon from '@mui/icons-material/Weekend';
import StyleIcon from '@mui/icons-material/Style';
import HdIcon from '@mui/icons-material/Hd';
import VideocamIcon from '@mui/icons-material/Videocam';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal'; // Icon for Magic Edit

// Components
import MaskingCanvas from './MaskingCanvas';
import { supabase, consumeCredits } from '../supabaseClient';
import { runComfyDeployWorkflow } from '../api/comfyDeployApi';
import { runGeminiImageGeneration, translateTextWithGemini, runGeminiMaskFreeEdit } from '../api/geminiApi'; // Import the new functions

// Tool option panels
import CropToolOptions from './editor/CropToolOptions';
import AdjustToolOptions from './editor/AdjustToolOptions';
// import AnnotateToolOptions from './editor/AnnotateToolOptions'; // Hidden for now
import FrameToolOptions from './editor/FrameToolOptions';
import LogoToolOptions from './editor/LogoToolOptions';
import MagicEraseToolOptions from './editor/MagicEraseToolOptions';
import MagicEditToolOptions from './editor/MagicEditToolOptions'; // Import the new tool options
import VirtualStagingToolOptions from './editor/VirtualStagingToolOptions';
import RestyleToolOptions from './editor/RestyleToolOptions';
import EnhanceToolOptions from './editor/EnhanceToolOptions';
import VideoToolOptions from './editor/VideoToolOptions';
import CreditAlert from '../components/CreditAlert';

// Styled components
const EditorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100%',
  backgroundColor: theme.palette.mode === 'dark' ? '#0A0A0F' : '#F5F5F5',
  position: 'relative',
  overflow: 'hidden',
  padding: 0,
  margin: 0
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.95)' : '#FFFFFF',
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'fixed', // Changed from sticky to fixed on mobile
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1200, // Increased z-index to ensure it's above everything
  height: '64px',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: '0px' // Hide scrollbar but allow scrolling
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 8px',
  }
}));

const ToolBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start', // Changed from center to flex-start
  padding: '8px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.95)' : '#FFFFFF',
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'fixed', // Changed from sticky to fixed on mobile
  top: '64px',
  left: 0,
  right: 0,
  zIndex: 1100, // Increased z-index to ensure it's above most elements
  overflowX: 'auto',
  gap: '8px',
  '&::-webkit-scrollbar': {
    height: '4px'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  },
  // Add padding to ensure items at the end are fully visible when scrolling
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '4px',
    paddingRight: '16px' // Add extra padding on the right for mobile
  }
}));

const ToolButton = styled(Button)(({ theme, active }) => ({
  minWidth: 'unset',
  borderRadius: '8px',
  padding: '8px 12px',
  backgroundColor: active ? 
    (theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.2)' : 'rgba(138, 43, 226, 0.1)') : 
    'transparent',
  border: active ? 
    `1px solid ${theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.5)' : 'rgba(138, 43, 226, 0.3)'}` : 
    '1px solid transparent',
  color: active ? 
    theme.palette.primary.main : 
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.15)' : 'rgba(138, 43, 226, 0.08)',
  },
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 500,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px'
}));

const CanvasArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? '#141420' : '#E5E5E5',
  position: 'relative',
  overflow: 'hidden',
  minHeight: 'calc(85vh - 120px)', // Increased from 70vh to 85vh to make bottom bigger
  maxHeight: 'calc(85vh - 120px)', // Increased max height as well
  width: '100%',
  '& img': {
    maxWidth: '95%',
    maxHeight: '95%',
    objectFit: 'contain'
  }
}));

const OptionsPanel = styled(Box)(({ theme, open }) => ({
  position: 'fixed',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'transparent',
  padding: 0,
  maxWidth: '320px',
  width: '100%',
  transition: 'all 0.3s ease',
  zIndex: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    bottom: '16px', // Moved up from 80px to 16px to be closer to bottom
    right: '50%',
    transform: 'translateX(50%)',
    top: 'auto',
    maxWidth: '90%',
    maxHeight: '60vh' // Reduced from 70vh to 60vh to show more of the preview area
  }
}));

const ToolGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  padding: '0 8px',
  alignItems: 'center',
  '&:not(:last-child)': {
    borderRight: `1px solid ${theme.palette.divider}`
  },
  [theme.breakpoints.down('md')]: {
    padding: '0 4px',
  }
}));

const ToolGroupLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.text.secondary,
  marginRight: '4px',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

// Main component
const ImageEditor = () => {
  const { imageId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  
  // State
  const [loading, setLoading] = useState(true);
  const [imageData, setImageData] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeTool, setActiveTool] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [optionsPanelOpen, setOptionsPanelOpen] = useState(false);
  const [isMaskingEnabled, setIsMaskingEnabled] = useState(false);
  const [maskUrl, setMaskUrl] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saveMenuAnchorEl, setSaveMenuAnchorEl] = useState(null);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState(null);
  const [showCreditAlert, setShowCreditAlert] = useState(false);
  const [creditAlertData, setCreditAlertData] = useState({ required: 0, available: 0 });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const [session, setSession] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogAction, setConfirmDialogAction] = useState(null);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState('');
  const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
  
  // Tool-specific states
  const [cropSettings, setCropSettings] = useState({ aspect: 1, unit: 'px' }); // Default to 1:1 aspect ratio
  const [adjustSettings, setAdjustSettings] = useState({
    brightness: 0, contrast: 0, saturation: 0, exposure: 0
  });
  // const [annotateSettings, setAnnotateSettings] = useState({
  //   tool: 'text', color: '#FF0000', fontSize: 16, lineWidth: 2, textPosition: 'middle-center'
  // });
  // const [previewText, setPreviewText] = useState('Örnek Metin'); // State for Annotate tool text
  const [frameSettings, setFrameSettings] = useState({
    style: 'simple', color: '#000000', width: 10
  });
  const [logoSettings, setLogoSettings] = useState({
    position: 'bottom-right', size: 20, opacity: 80, logoUrl: null, logoId: null
  });
  const [magicEraseSettings, setMagicEraseSettings] = useState({
    brushSize: 20, prompt: ''
  });
  const [magicEditSettings, setMagicEditSettings] = useState({ // State for the new tool
    prompt: ''
  });
  const [virtualStagingSettings, setVirtualStagingSettings] = useState({
    roomType: 'Living Room', style: 'contemporary', colorTheme: 'original'
  });
  const [restyleSettings, setRestyleSettings] = useState({
    roomType: 'Living Room', style: 'contemporary', colorTheme: 'original',
    referenceImage: null
  });
  const [enhanceSettings, setEnhanceSettings] = useState({
    resolution: '8k', sharpness: 75, noiseReduction: 75
  });
  const [videoSettings, setVideoSettings] = useState({
    cameraMovement: 'pan_right', duration: '4', quality: 'standard'
  });
  
  // Fetch session on mount
  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      
      if (!data.session) {
        navigate('/login', { 
          state: { 
            returnUrl: `/editor/${imageId}`,
            message: 'Düzenleyiciyi kullanmak için lütfen giriş yapın'
          }
        });
      }
    };
    
    getSession();
    
    // Make openProfileModal available globally for the LogoToolOptions component
    window.openProfileModal = () => {
      // Dispatch an event that can be caught by the parent component
      const event = new CustomEvent('openProfileModal');
      window.dispatchEvent(event);
    };
    
    return () => {
      // Clean up global function
      delete window.openProfileModal;
    };
  }, [imageId, navigate]);
  
  // Fetch image data
  useEffect(() => {
    const fetchImage = async () => {
      if (!imageId || !session) return;
      
      // Check if we have an image URL passed via location state
      const passedImageUrl = location.state?.imageUrl;
      
      if (passedImageUrl) {
        try {
          setLoading(true);
          
          // Create a placeholder image data object
          const placeholderData = {
            id: imageId,
            url: passedImageUrl,
            user_id: session.user.id,
            prompt: location.state?.prompt || 'Düzenlenmiş görsel',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          setImageData(placeholderData);
          
          // Load the image
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = passedImageUrl;
          
          img.onload = () => {
            setOriginalImage(passedImageUrl);
            setCurrentImage(passedImageUrl);
            imageRef.current = img;
            
            // Initialize canvas if needed
            if (canvasRef.current) {
              initCanvas();
            }
            
            // Add first history entry
            addToHistory(passedImageUrl);
            setLoading(false);
          };
          
          img.onerror = (err) => {
            console.error('Görsel yüklenirken hata oluştu:', err);
            setLoading(false);
            showSnackbar('Görsel yüklenemedi. Lütfen tekrar deneyin.', 'error');
          };
          
          return; // Exit early since we're using the passed image
        } catch (error) {
          console.error('Geçirilen görsel işlenirken hata oluştu:', error);
          // Continue to normal fetch flow if there's an error with the passed image
        }
      }
      
      try {
        setLoading(true);
        
        // Fetch image data from database
        const { data, error } = await supabase
          .from('images')
          .select('*')
          .eq('id', imageId)
          .single();
          
        if (error) throw error;
        
        if (!data) {
          throw new Error('Görsel bulunamadı');
        }
        
        // Check if user has permission to edit this image
        if (data.user_id !== session.user.id) {
          throw new Error('Bu görseli düzenleme yetkiniz yok');
        }
        
        setImageData(data);
        
        // Load the image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = data.url;
        
        img.onload = () => {
          setOriginalImage(data.url);
          setCurrentImage(data.url);
          imageRef.current = img;
          
          // Initialize canvas if needed
          if (canvasRef.current) {
            initCanvas();
          }
          
          // Add first history entry
          addToHistory(data.url);
          setLoading(false);
        };
        
        img.onerror = (err) => {
          console.error('Görsel yüklenirken hata oluştu:', err);
          setLoading(false);
          showSnackbar('Görsel yüklenemedi. Lütfen tekrar deneyin.', 'error');
        };
        
      } catch (error) {
        console.error('Görsel verisi alınırken hata oluştu:', error);
        setLoading(false);
        showSnackbar(error.message || 'Görsel yüklenemedi', 'error');
        navigate(-1);
      }
    };
    
    fetchImage();
  }, [imageId, session, navigate]);
  
  // Initialize canvas
  const initCanvas = useCallback(() => {
    if (!canvasRef.current || !imageRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions based on image
    canvas.width = imageRef.current.naturalWidth;
    canvas.height = imageRef.current.naturalHeight;
    
    // Draw image
    ctx.drawImage(imageRef.current, 0, 0);
  }, []);
  
  // Add state to history
  const addToHistory = useCallback((imageUrl) => {
    // If we're not at the end of history, truncate
    const newHistory = history.slice(0, historyIndex + 1);
    setHistory([...newHistory, imageUrl]);
    setHistoryIndex(newHistory.length);
  }, [history, historyIndex]);
  
  // Handle undo
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0) return;
    
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setCurrentImage(history[newIndex]);
  }, [history, historyIndex]);
  
  // Handle redo
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1) return;
    
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    setCurrentImage(history[newIndex]);
  }, [history, historyIndex]);
  
  // Handle zoom
  const handleZoom = useCallback((direction) => {
    setZoom(prev => {
      const newZoom = direction === 'in' ? prev * 1.2 : prev / 1.2;
      return Math.max(0.1, Math.min(5, newZoom));
    });
  }, []);
  
  // Image dragging handlers
  const handleMouseDown = useCallback((e) => {
    if (activeTool) return; // Don't allow dragging when a tool is active
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  }, [activeTool]);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const dx = (e.clientX - dragStart.x) / zoom;
    const dy = (e.clientY - dragStart.y) / zoom;
    
    setPosition(prev => ({
      x: prev.x + dx,
      y: prev.y + dy
    }));
    
    setDragStart({ x: e.clientX, y: e.clientY });
  }, [isDragging, dragStart, zoom]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Touch event handlers for mobile
  const handleTouchStart = useCallback((e) => {
    if (activeTool) return; // Don't allow dragging when a tool is active
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({ 
        x: e.touches[0].clientX, 
        y: e.touches[0].clientY 
      });
    }
  }, [activeTool]);
  
  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length !== 1) return;
    
    const dx = (e.touches[0].clientX - dragStart.x) / zoom;
    const dy = (e.touches[0].clientY - dragStart.y) / zoom;
    
    setPosition(prev => ({
      x: prev.x + dx,
      y: prev.y + dy
    }));
    
    setDragStart({ 
      x: e.touches[0].clientX, 
      y: e.touches[0].clientY 
    });
    
    // Prevent default to stop page scrolling
    e.preventDefault();
  }, [isDragging, dragStart, zoom]);
  
  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Handle tool selection
  const handleToolSelect = useCallback((tool) => {
    if (activeTool === tool) {
      setActiveTool(null);
      setOptionsPanelOpen(false);
      setIsMaskingEnabled(false);
      
      // When closing a tool panel, ensure the top toolbar is visible
      if (window.innerWidth < 600) {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }, 100);
      }
    } else {
      setActiveTool(tool);
      setOptionsPanelOpen(true);
      
      // Enable masking for magic erase tool
      if (tool === 'magicErase') {
        setIsMaskingEnabled(true);
      } else {
        setIsMaskingEnabled(false); // Ensure masking is off for other tools
      }

      // Scroll to a position that keeps the top toolbar visible when a tool is selected
      if (window.innerWidth < 600) {
        // Use a small timeout to ensure UI updates first
        setTimeout(() => {
          // Scroll to a position that keeps the top toolbar visible
          window.scrollTo({
            top: 80, // Position that keeps the toolbar visible
            behavior: 'smooth'
          });
        }, 100);
      }
    }
  }, [activeTool]);
  
  // Handle save menu
  const handleSaveMenuOpen = (event) => {
    setSaveMenuAnchorEl(event.currentTarget);
  };
  
  const handleSaveMenuClose = () => {
    setSaveMenuAnchorEl(null);
  };
  
  // Handle more menu
  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchorEl(event.currentTarget);
  };
  
  const handleMoreMenuClose = () => {
    setMoreMenuAnchorEl(null);
  };
  
  // Handle save operations
  const handleSave = async () => {
    handleSaveMenuClose();
    
    // Show confirmation dialog
    setConfirmDialogTitle('Görseli Kaydet');
    setConfirmDialogMessage('Orijinal görselin üzerine yazılacak. Bu işlem geri alınamaz. Devam etmek istiyor musunuz?');
    setConfirmDialogAction('save');
    setConfirmDialogOpen(true);
  };
  
  const handleSaveAs = async () => {
    handleSaveMenuClose();
    
    // Show confirmation dialog
    setConfirmDialogTitle('Kopya Olarak Kaydet');
    setConfirmDialogMessage('Görsel kütüphanenize yeni bir kopya olarak kaydedilecek. Devam etmek istiyor musunuz?');
    setConfirmDialogAction('saveAs');
    setConfirmDialogOpen(true);
  };
  
  const handleDownload = async () => { // Make the function async
    handleSaveMenuClose();
    
    if (!currentImage) return;
    
    try {
      // Fetch the image data
      const response = await fetch(currentImage);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      
      // Create an object URL for the blob
      const objectUrl = URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `edited-${imageData?.id || 'image'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the object URL to free up memory
      URL.revokeObjectURL(objectUrl);
      
      showSnackbar('Görsel başarıyla indirildi', 'success');
      
    } catch (error) {
      console.error('İndirme sırasında hata:', error);
      showSnackbar('Görsel indirilemedi. Lütfen tekrar deneyin.', 'error');
    }
  };
  
  // Handle confirmation dialog actions
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };
  
  const handleConfirmAction = async () => {
    setConfirmDialogOpen(false);
    
    try {
      setProcessing(true);
      
      if (confirmDialogAction === 'save') {
        // Update the original image in the database
        const savedImageUrl = await saveImageToSupabase(true);
        if (savedImageUrl) {
          // Create a new image object to force reload
          const newImg = new Image();
          newImg.crossOrigin = 'anonymous';
          
          // Disable browser caching
          newImg.src = savedImageUrl;
          
          console.log('Loading saved image:', savedImageUrl);
          
          // Wait for the image to load before updating state
          await new Promise((resolve, reject) => {
            newImg.onload = () => {
              console.log('New image loaded successfully, dimensions:', newImg.width, 'x', newImg.height);
              // Update image reference
              imageRef.current = newImg;
              // Update current image
              setCurrentImage(savedImageUrl);
              // Do NOT update originalImage on overwrite, it should remain the initial image
              
              // Initialize canvas with the new image if needed
              if (canvasRef.current) {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                canvas.width = newImg.naturalWidth || newImg.width;
                canvas.height = newImg.naturalHeight || newImg.height;
                ctx.drawImage(newImg, 0, 0);
                console.log('Canvas updated with new image');
              }
              
              resolve();
            };
            newImg.onerror = (err) => {
              console.error('Error loading saved image:', err);
              reject(new Error('Failed to load saved image'));
            };
          });
          
          showSnackbar('Görsel başarıyla kaydedildi', 'success');
        }
      } else if (confirmDialogAction === 'saveAs') {
        // Save as a new image
        const savedImageUrl = await saveImageToSupabase(false);
        if (savedImageUrl) {
          // Create a new image object to force reload
          const newImg = new Image();
          newImg.crossOrigin = 'anonymous';
          
          // Disable browser caching
          newImg.src = savedImageUrl;
          
          console.log('Loading saved image:', savedImageUrl);
          
          // Wait for the image to load before updating state
          await new Promise((resolve, reject) => {
            newImg.onload = () => {
              console.log('New image loaded successfully, dimensions:', newImg.width, 'x', newImg.height);
              // Update image reference
              imageRef.current = newImg;
              // Update current image and original image
              setCurrentImage(savedImageUrl);
              setOriginalImage(savedImageUrl);
              
              // Initialize canvas with the new image if needed
              if (canvasRef.current) {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                canvas.width = newImg.naturalWidth || newImg.width;
                canvas.height = newImg.naturalHeight || newImg.height;
                ctx.drawImage(newImg, 0, 0);
                console.log('Canvas updated with new image');
              }
              
              resolve();
            };
            newImg.onerror = (err) => {
              console.error('Error loading saved image:', err);
              reject(new Error('Failed to load saved image'));
            };
          });
          
          showSnackbar('Görsel kopyası başarıyla kaydedildi', 'success');
        }
      }
    } catch (error) {
      console.error('Kaydetme işlemi sırasında hata:', error);
      showSnackbar('Kaydetme işlemi başarısız oldu', 'error');
    } finally {
      setProcessing(false);
    }
  };
  
  // Save image to Supabase
  const saveImageToSupabase = async (overwrite = false) => {
    if (!currentImage || !session || !imageData) return null;
    
    try {
      console.log('Starting save to Supabase...');
      
      // Convert data URL to blob if needed
      let imageBlob;
      if (currentImage.startsWith('data:')) {
        console.log('Converting data URL to blob...');
        const response = await fetch(currentImage);
        imageBlob = await response.blob();
      } else {
        console.log('Fetching image from URL...');
        // Add cache-busting to fetch to ensure we get the latest version
        const fetchUrl = currentImage.includes('?') 
          ? `${currentImage}&t=${Date.now()}` 
          : `${currentImage}?t=${Date.now()}`;
        const response = await fetch(fetchUrl, { cache: 'no-store' });
        imageBlob = await response.blob();
      }
      
      console.log('Image blob created, size:', imageBlob.size);
      
      // Generate filename
      const fileName = overwrite 
        ? imageData.url.split('/').pop().split('?')[0] // Remove any query params
        : `edited-${Date.now()}.png`;
      
      console.log('Using filename:', fileName);
      
      // Upload to Supabase Storage
      console.log('Uploading to Supabase Storage...');
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('generated-images')
        .upload(fileName, imageBlob, {
          upsert: overwrite,
          cacheControl: 'no-cache'
        });
        
      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }
      
      console.log('Upload successful:', uploadData);
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('generated-images')
        .getPublicUrl(fileName);
      
      console.log('Public URL:', publicUrl);
      
      // Add cache-busting parameter to ensure we get the latest version
      const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
      
      if (overwrite) {
        // Update existing record
        console.log('Updating existing record in database...');
        
        // Check if the ID is a valid UUID before updating
        const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(imageData.id);
        
        if (!isValidUUID) {
          console.log('ID is not a valid UUID, creating a new record instead:', imageData.id);
          // Create new record instead of updating
          const { data: newImage, error: insertError } = await supabase
            .from('images')
            .insert({
              url: publicUrl, // Store the clean URL in the database
              user_id: session.user.id,
              prompt: imageData.prompt,
              property_id: imageData.property_id,
              property_name: imageData.property_name,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();
            
          if (insertError) {
            console.error('Database insert error:', insertError);
            throw insertError;
          }
          
          console.log('Database insert successful:', newImage);
          
          // Update local state
          setImageData(newImage);
        } else {
          // Proceed with update for valid UUID
          const { data: updateData, error: updateError } = await supabase
            .from('images')
            .update({
              url: publicUrl, // Store the clean URL in the database
              updated_at: new Date().toISOString()
            })
            .eq('id', imageData.id)
            .select();
            
          if (updateError) {
            console.error('Database update error:', updateError);
            throw updateError;
          }
          
          console.log('Database update successful:', updateData);
          
          // Update local state
          setImageData({
            ...imageData,
            url: publicUrl,
            updated_at: new Date().toISOString()
          });
        }
        
        
      } else {
        // Create new record
        console.log('Creating new record in database...');
        const { data: newImage, error: insertError } = await supabase
          .from('images')
          .insert({
            url: publicUrl, // Store the clean URL in the database
            user_id: session.user.id,
            prompt: imageData.prompt,
            property_id: imageData.property_id,
            property_name: imageData.property_name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
          
        if (insertError) {
          console.error('Database insert error:', insertError);
          throw insertError;
        }
        
        console.log('Database insert successful:', newImage);
        
        // Update local state
        setImageData(newImage);
      }
      
      // Reset history with the cache-busted URL to ensure we load the latest version
      setHistory([cacheBustedUrl]);
      setHistoryIndex(0);
      
      console.log('Save to Supabase completed successfully');
      return cacheBustedUrl;
    } catch (error) {
      console.error('Supabase kaydetme hatası:', error);
      throw error;
    }
  };
  
  // Image processing functions
  const applyImageCrop = (cropSettings) => {
    if (!currentImage || !imageRef.current) return;
    
    try {
      // Create a temporary canvas for cropping
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Create a new image object from the currentImage URL
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle potential CORS issues
      img.src = currentImage;

      img.onload = () => {
        // Get the image dimensions
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;
        const targetAspect = cropSettings.aspect; // Get the target aspect ratio

        if (!targetAspect) {
          showSnackbar('Geçerli bir en/boy oranı seçilmedi.', 'warning');
          return; // Don't crop if aspect is somehow null/undefined
        }

        // Calculate the dimensions and position for the centered crop
        let cropWidth, cropHeight, cropX, cropY;
        const originalAspect = imgWidth / imgHeight;

        if (targetAspect > originalAspect) {
          // Target aspect is wider than original: Use full width, calculate height
          cropWidth = imgWidth;
          cropHeight = imgWidth / targetAspect;
          cropX = 0;
          cropY = (imgHeight - cropHeight) / 2;
        } else {
          // Target aspect is narrower or same as original: Use full height, calculate width
          cropHeight = imgHeight;
          cropWidth = imgHeight * targetAspect;
          cropX = (imgWidth - cropWidth) / 2;
          cropY = 0;
        }

        // Ensure calculated dimensions are integers
        cropWidth = Math.round(cropWidth);
        cropHeight = Math.round(cropHeight);
        cropX = Math.round(cropX);
        cropY = Math.round(cropY);

        // Set canvas dimensions to the final crop size
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Draw the calculated cropped portion of the image onto the canvas
        ctx.drawImage(
          img,      // Source image
          cropX,    // Source X
          cropY,    // Source Y
          cropWidth, // Source Width
          cropHeight, // Source Height
          0,        // Destination X
          0,        // Destination Y
          cropWidth, // Destination Width
          cropHeight  // Destination Height
        );

        // Convert canvas to data URL
        const croppedImageUrl = canvas.toDataURL('image/png');

        // Update the current image and add to history
        setCurrentImage(croppedImageUrl);
        addToHistory(croppedImageUrl);

        // Update the image reference for potential subsequent operations
        const newImgRef = new Image();
        newImgRef.src = croppedImageUrl;
        imageRef.current = newImgRef; // Update the main image ref

        showSnackbar('Kırpma işlemi başarıyla uygulandı', 'success');
      };

      img.onerror = (error) => {
        console.error('Kırpma için görsel yüklenirken hata oluştu:', error);
        showSnackbar('Kırpma işlemi başarısız oldu (görsel yüklenemedi)', 'error');
      };

    } catch (error) {
      console.error('Kırpma işlemi sırasında hata:', error);
      showSnackbar('Kırpma işlemi başarısız oldu', 'error');
    }
  };
  
  const applyImageAdjustments = (adjustSettings) => {
    if (!currentImage || !imageRef.current) return;
    
    try {
      // Create a temporary canvas for adjustments
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Create a new image object from the currentImage URL
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle potential CORS issues
      img.src = currentImage;

      img.onload = () => {
        // Get the image dimensions
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        // Draw the image to the canvas
        ctx.drawImage(img, 0, 0);

        // Apply adjustments using CSS filters
        // We'll use a second canvas with the filter applied
      const filteredCanvas = document.createElement('canvas');
      const filteredCtx = filteredCanvas.getContext('2d');
      filteredCanvas.width = canvas.width;
      filteredCanvas.height = canvas.height;
      
      // Set the filter based on adjustment settings
      const { brightness, contrast, saturation, exposure } = adjustSettings;
      filteredCtx.filter = `
        brightness(${1 + brightness / 100})
        contrast(${1 + contrast / 100})
        saturate(${1 + saturation / 100})
        ${exposure !== 0 ? `brightness(${1 + exposure / 200})` : ''}
      `;
      
      // Draw the original canvas onto the filtered canvas
      filteredCtx.drawImage(canvas, 0, 0);
      
      // Convert filtered canvas to data URL
      const adjustedImageUrl = filteredCanvas.toDataURL('image/png');
      
      // Update the current image and add to history
      setCurrentImage(adjustedImageUrl);
      addToHistory(adjustedImageUrl);
      
      // Update the image reference for potential subsequent operations
      const newImgRef = new Image();
      newImgRef.src = adjustedImageUrl;
      imageRef.current = newImgRef; // Update the main image ref

      showSnackbar('Ayarlar başarıyla uygulandı', 'success');
    };

    img.onerror = (error) => {
      console.error('Ayar için görsel yüklenirken hata oluştu:', error);
      showSnackbar('Ayarlama işlemi başarısız oldu (görsel yüklenemedi)', 'error');
    };

    } catch (error) {
      console.error('Ayarlama işlemi sırasında hata:', error);
      showSnackbar('Ayarlama işlemi başarısız oldu', 'error');
    }
  };
  
  // const applyImageAnnotation = (annotateSettings, text = 'Örnek Metin') => {
  //   if (!currentImage) return;
    
  //   try {
  //     // Create a temporary canvas for annotation
  //     const canvas = document.createElement('canvas');
  //     const ctx = canvas.getContext('2d');
      
  //     // Create a new image and properly wait for it to load
  //     return new Promise((resolve) => {
  //       const img = new Image();
  //       img.crossOrigin = 'anonymous';
  //       img.src = currentImage;
        
  //       img.onload = () => {
  //         // Set canvas dimensions based on the loaded image
  //         canvas.width = img.naturalWidth || img.width;
  //         canvas.height = img.naturalHeight || img.height;
          
  //         // Draw the image to the canvas
  //         ctx.drawImage(img, 0, 0);
          
  //         // Set styles based on settings
  //         ctx.strokeStyle = annotateSettings.color;
  //         ctx.fillStyle = annotateSettings.color;
  //         ctx.lineWidth = annotateSettings.lineWidth;
          
  //         // Calculate position based on textPosition setting
  //         let posX = canvas.width / 2;
  //         let posY = canvas.height / 2;
          
  //         // Adjust position based on textPosition
  //         if (annotateSettings.textPosition) {
  //           switch (annotateSettings.textPosition) {
  //             case 'top-left':
  //               posX = canvas.width * 0.1;
  //               posY = canvas.height * 0.1;
  //               break;
  //             case 'top-center':
  //               posX = canvas.width * 0.5;
  //               posY = canvas.height * 0.1;
  //               break;
  //             case 'top-right':
  //               posX = canvas.width * 0.9;
  //               posY = canvas.height * 0.1;
  //               break;
  //             case 'middle-left':
  //               posX = canvas.width * 0.1;
  //               posY = canvas.height * 0.5;
  //               break;
  //             case 'middle-center':
  //               posX = canvas.width * 0.5;
  //               posY = canvas.height * 0.5;
  //               break;
  //             case 'middle-right':
  //               posX = canvas.width * 0.9;
  //               posY = canvas.height * 0.5;
  //               break;
  //             case 'bottom-left':
  //               posX = canvas.width * 0.1;
  //               posY = canvas.height * 0.9;
  //               break;
  //             case 'bottom-center':
  //               posX = canvas.width * 0.5;
  //               posY = canvas.height * 0.9;
  //               break;
  //             case 'bottom-right':
  //               posX = canvas.width * 0.9;
  //               posY = canvas.height * 0.9;
  //               break;
  //             default:
  //               // Default to center
  //               posX = canvas.width * 0.5;
  //               posY = canvas.height * 0.5;
  //           }
  //         }
          
  //         // Draw annotation based on tool type
  //         if (annotateSettings.tool === 'text') {
  //           ctx.font = `${annotateSettings.fontSize}px Arial`;
  //           ctx.textAlign = 'center';
  //           ctx.textBaseline = 'middle';
  //           ctx.fillText(text, posX, posY);
  //         } 
  //         else if (annotateSettings.tool === 'draw') {
  //           // Draw a sample line
  //           ctx.beginPath();
  //           ctx.moveTo(posX - 100, posY);
  //           ctx.quadraticCurveTo(posX, posY - 50, posX + 100, posY);
  //           ctx.stroke();
  //         } 
  //         else if (annotateSettings.tool === 'arrow') {
  //           // Draw an arrow
  //           const arrowLength = 150;
  //           const arrowHeadLength = 20;
  //           const arrowHeadWidth = 10;
            
  //           // Draw the line
  //           ctx.beginPath();
  //           ctx.moveTo(posX - arrowLength/2, posY);
  //           ctx.lineTo(posX + arrowLength/2 - arrowHeadLength, posY);
  //           ctx.stroke();
            
  //           // Draw the arrowhead
  //           ctx.beginPath();
  //           ctx.moveTo(posX + arrowLength/2, posY);
  //           ctx.lineTo(posX + arrowLength/2 - arrowHeadLength, posY - arrowHeadWidth);
  //           ctx.lineTo(posX + arrowLength/2 - arrowHeadLength, posY + arrowHeadWidth);
  //           ctx.closePath();
  //           ctx.fill();
  //         }
          
  //         // Convert canvas to data URL
  //         const annotatedImageUrl = canvas.toDataURL('image/png');
          
  //         // Update the current image and add to history
  //         setCurrentImage(annotatedImageUrl);
  //         addToHistory(annotatedImageUrl);
          
  //         // Update the image reference for potential subsequent operations
  //         const newImgRef = new Image();
  //         newImgRef.src = annotatedImageUrl;
  //         imageRef.current = newImgRef; // Update the main image ref

  //         // Reset the preview text for next annotation
  //         // setPreviewText('Örnek Metin'); // Commented out as state is removed
          
  //         showSnackbar('Not ekleme işlemi başarıyla uygulandı', 'success');
          
  //         // Close the tool panel
  //         setActiveTool(null);
  //         setOptionsPanelOpen(false);
          
  //         resolve();
  //       };
        
  //       img.onerror = (error) => {
  //         console.error('Görsel yüklenirken hata oluştu:', error);
  //         showSnackbar('Not ekleme işlemi başarısız oldu', 'error');
  //         resolve();
  //       };
  //     });
      
  //   } catch (error) {
  //     console.error('Not ekleme işlemi sırasında hata:', error);
  //     showSnackbar('Not ekleme işlemi başarısız oldu', 'error');
  //   }
  // };
  
  const applyImageFrame = (frameSettings) => {
    if (!currentImage || !imageRef.current) return;
    
    try {
      // Create a temporary canvas for framing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Create a new image object from the currentImage URL
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle potential CORS issues
      img.src = currentImage;

      img.onload = () => {
        // Get the image dimensions
        const imgWidth = img.naturalWidth;
        const imgHeight = img.naturalHeight;

        // Calculate frame dimensions
        const frameWidth = frameSettings.width;
      const totalWidth = img.naturalWidth + (frameWidth * 2);
      const totalHeight = img.naturalHeight + (frameWidth * 2);
      
      // Set canvas dimensions to include the frame
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      
      // Draw frame background
      ctx.fillStyle = frameSettings.color;
      ctx.fillRect(0, 0, totalWidth, totalHeight);
      
      // Draw the image in the center
      ctx.drawImage(img, frameWidth, frameWidth, imgWidth, imgHeight);

      // Apply special frame styles
      if (frameSettings.style === 'double') {
        // Draw inner border
        ctx.strokeStyle = frameSettings.color;
        ctx.lineWidth = frameWidth / 3;
        const offset = frameWidth / 2;
        ctx.strokeRect(offset, offset, totalWidth - offset * 2, totalHeight - offset * 2);
      } else if (frameSettings.style === 'shadow') {
        // Add shadow effect
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = frameWidth;
        ctx.shadowOffsetX = frameWidth / 4;
        ctx.shadowOffsetY = frameWidth / 4;
        ctx.fillRect(0, 0, 0, 0); // Trigger shadow
      }
      
      // Convert canvas to data URL
      const framedImageUrl = canvas.toDataURL('image/png');
      
      // Update the current image and add to history
      setCurrentImage(framedImageUrl);
      addToHistory(framedImageUrl);
      
      // Update the image reference for potential subsequent operations
      const newImgRef = new Image();
      newImgRef.src = framedImageUrl;
      imageRef.current = newImgRef; // Update the main image ref

      showSnackbar('Çerçeve başarıyla uygulandı', 'success');
    };

    img.onerror = (error) => {
      console.error('Çerçeve için görsel yüklenirken hata oluştu:', error);
      showSnackbar('Çerçeveleme işlemi başarısız oldu (görsel yüklenemedi)', 'error');
    };

    } catch (error) {
      console.error('Çerçeveleme işlemi sırasında hata:', error);
      showSnackbar('Çerçeveleme işlemi başarısız oldu', 'error');
    }
  };
  
  const applyImageLogo = (logoSettings) => {
    if (!currentImage || !imageRef.current || !logoSettings.logoUrl) return;
    
    try {
      // Create a temporary canvas for adding logo
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      return new Promise((resolve) => {
        // Create a new image for the current image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = currentImage;
        
        img.onload = () => {
          // Set canvas dimensions based on the image
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          
          // Draw the image to the canvas
          ctx.drawImage(img, 0, 0);
          
          // Create a new image for the logo
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.src = logoSettings.logoUrl;
          
          logoImg.onload = () => {
            // Calculate logo dimensions and position
            const logoWidth = (canvas.width * logoSettings.size) / 100;
            const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
            
            let logoX, logoY;
            
            // Position the logo based on settings
            switch (logoSettings.position) {
              case 'top-left':
                logoX = canvas.width * 0.05;
                logoY = canvas.height * 0.05;
                break;
              case 'top-right':
                logoX = canvas.width * 0.95 - logoWidth;
                logoY = canvas.height * 0.05;
                break;
              case 'bottom-left':
                logoX = canvas.width * 0.05;
                logoY = canvas.height * 0.95 - logoHeight;
                break;
              case 'bottom-right':
              default:
                logoX = canvas.width * 0.95 - logoWidth;
                logoY = canvas.height * 0.95 - logoHeight;
                break;
            }
            
            // Set global alpha for transparency
            ctx.globalAlpha = logoSettings.opacity / 100;
            
            // Draw the logo
            ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
            
            // Reset global alpha
            ctx.globalAlpha = 1.0;
            
            // Convert canvas to data URL
            const logoImageUrl = canvas.toDataURL('image/png');
            
            // Update the current image and add to history
            setCurrentImage(logoImageUrl);
            addToHistory(logoImageUrl);
            
            // Update the image reference for potential subsequent operations
            const newImgRef = new Image();
            newImgRef.src = logoImageUrl;
            imageRef.current = newImgRef; // Update the main image ref

            showSnackbar('Logo başarıyla eklendi', 'success');
            
            // Close the tool panel
            setActiveTool(null);
            setOptionsPanelOpen(false);
            
            resolve();
          };
          
          logoImg.onerror = (error) => {
            console.error('Logo yüklenirken hata oluştu:', error);
            showSnackbar('Logo ekleme işlemi başarısız oldu', 'error');
            resolve();
          };
        };
        
        img.onerror = (error) => {
          console.error('Görsel yüklenirken hata oluştu:', error);
          showSnackbar('Logo ekleme işlemi başarısız oldu', 'error');
          resolve();
        };
      });
    } catch (error) {
      console.error('Logo ekleme işlemi sırasında hata:', error);
      showSnackbar('Logo ekleme işlemi başarısız oldu', 'error');
    }
  };
  
  // Handle mask save
  const handleSaveMask = (mask, maskUrl) => {
    console.log('Maske kaydedildi:', maskUrl);
    setMaskUrl(maskUrl);
    setIsMaskingEnabled(false);
    showSnackbar('Maske başarıyla kaydedildi', 'success');
  };
  
  // Show snackbar message
  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  // Execute AI workflow
  const executeAIWorkflow = async (workflowId, inputs, requiredCredits) => {
    if (processing) return;
    
    try {
      // Check user credits
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('credits_remaining')
        .eq('id', user.id)
        .single();
        
      if (profileError) {
        throw new Error('Kullanıcı profili alınamadı');
      }
      
      if (profile.credits_remaining < requiredCredits) {
        setCreditAlertData({ 
          required: requiredCredits, 
          available: profile.credits_remaining 
        });
        setShowCreditAlert(true);
        return;
      }
      
      // Start processing
      setProcessing(true);
      setProgress(0);
      
      // On mobile, scroll to a position that keeps the toolbar visible
      if (window.innerWidth < 600) {
        setTimeout(() => {
          // Scroll to a position that keeps the top toolbar visible
          window.scrollTo({
            top: 80, // Position that keeps the toolbar visible
            behavior: 'smooth'
          });
        }, 100);
      }
      
      // Consume credits BEFORE starting the potentially long-running API call
      await consumeCredits(user.id, requiredCredits);

      // Determine which API to use based on workflowId or tool type
      let result;
      let finalPromptUsed = inputs.prompt || ''; // Keep track of the prompt used

      if (workflowId === 'gemini-image-generation') { // Virtual Staging uses this
        // Use Gemini Image Generation API
        const { publicUrl, imageGenPrompt } = await runGeminiImageGeneration(
          inputs.prompt || 'Furnish this room', // Default prompt if needed
          currentImage,
          { // Pass relevant options
            roomType: inputs.roomType,
            style: inputs.style,
            furnitureColor: inputs.furnitureColor,
            isRoomFurnished: inputs.isRoomFurnished // Pass if needed
          },
          (prog, _, message) => setProgress(prog * 100) // Use progress callback
        );
        result = publicUrl;
        finalPromptUsed = imageGenPrompt; // Store the actual prompt used by the API
      } else if (workflowId === 'gemini-mask-free-edit') { // Magic Edit uses this
        // Use Gemini Mask-Free Edit API
        const { publicUrl, imageGenPrompt } = await runGeminiMaskFreeEdit(
          inputs.prompt, // The translated prompt from MagicEditToolOptions
          currentImage,
          (prog, _, message) => setProgress(prog * 100) // Use progress callback
        );
        result = publicUrl;
        finalPromptUsed = imageGenPrompt; // Store the actual prompt used by the API
      }
      else {
        // Use ComfyDeploy API for other workflows (Magic Erase, Restyle, Enhance, Video)
        const comfyInputs = {
            input_image: currentImage, // Always include the current image
            ...inputs // Spread the rest of the inputs (mask, prompts, etc.)
        };
        // Remove prompt if it was only for Gemini edit/gen, Comfy uses 'positive'/'negative'
        // delete comfyInputs.prompt;

        const outputs = await runComfyDeployWorkflow(
          workflowId,
          comfyInputs,
          (prog, _, message) => setProgress(prog * 100) // Use progress callback
        );

        // Extract result URL from ComfyDeploy outputs
        if (outputs && outputs.length > 0) {
          let outputUrl = null;
          // Look for image URLs in common output structures
          for (const output of outputs) {
            if (output?.data?.images?.[0]?.url) {
              outputUrl = output.data.images[0].url;
              break;
            } else if (output?.data?.gifs?.[0]?.url) { // Handle video/gif output
              outputUrl = output.data.gifs[0].url;
              break;
            } else if (output?.url) { // Fallback for direct URL output
              outputUrl = output.url;
              break;
            }
          }
          if (!outputUrl) {
            console.error("ComfyDeploy output structure:", outputs);
            throw new Error('ComfyDeploy API yanıtında görsel/video URL\'i bulunamadı');
          }
          result = outputUrl;
        } else {
          console.error("ComfyDeploy empty/invalid output:", outputs);
          throw new Error('ComfyDeploy API yanıtı boş veya geçersiz');
        }
      }

      // --- Update image state after successful API call ---
      if (result) {
        // Add cache-busting parameter to the result URL
        const cacheBustedResultUrl = `${result}?t=${Date.now()}`;

        // Preload the new image before updating state to avoid flicker
        const newImg = new Image();
        newImg.crossOrigin = 'anonymous';
        newImg.src = cacheBustedResultUrl;

        await new Promise((resolve, reject) => {
            newImg.onload = () => {
                console.log('AI result image loaded successfully');
                imageRef.current = newImg; // Update image ref
                setCurrentImage(cacheBustedResultUrl);
                addToHistory(cacheBustedResultUrl);
                resolve();
            };
            newImg.onerror = (err) => {
                console.error('Error loading AI result image:', err);
                reject(new Error('Failed to load AI result image'));
            };
        });

        showSnackbar('İşlem başarıyla tamamlandı', 'success');
      } else {
         throw new Error('AI işlemi bir sonuç döndürmedi.');
      }

    } catch (error) {
      console.error('AI iş akışı hatası:', error);
      // Attempt to consume credits back if the error occurred *after* the initial consumption
      // This is complex and needs careful implementation on the backend/Supabase function side
      // For now, just show the error.
      showSnackbar(`İşlem başarısız oldu: ${error.message}`, 'error');
    } finally {
      setProcessing(false);
      setProgress(0);
      
      // Ensure the top toolbar is visible after processing completes
      if (window.innerWidth < 600) {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }, 300);
      }
    }
  };
  
  // Render options panel based on active tool
  const renderOptionsPanel = () => {
    if (!activeTool || !optionsPanelOpen) return null;
    
    switch (activeTool) {
      case 'crop':
        return (
          <CropToolOptions 
            settings={cropSettings}
            onChange={setCropSettings}
            currentImage={currentImage}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Apply crop logic
                applyImageCrop(cropSettings);
              }
              // Close the panel regardless of shouldApply value
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      case 'adjust':
        return (
          <AdjustToolOptions 
            settings={adjustSettings}
            onChange={setAdjustSettings}
            currentImage={currentImage}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Apply adjustments
                applyImageAdjustments(adjustSettings);
              }
              // Close the panel regardless of shouldApply value
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      // case 'annotate': // Hidden for now
      //   return (
      //     <AnnotateToolOptions 
      //       settings={annotateSettings}
      //       onChange={setAnnotateSettings}
      //       currentImage={currentImage}
      //       onTextChange={setPreviewText}
      //       onApply={(shouldApply) => {
      //         if (shouldApply) {
      //           // Apply annotation
      //           applyImageAnnotation(annotateSettings, previewText);
      //         } else {
      //           // Just close the panel without applying
      //           setActiveTool(null);
      //           setOptionsPanelOpen(false);
      //         }
      //       }}
      //     />
      //   );
        
      case 'frame':
        return (
          <FrameToolOptions 
            settings={frameSettings}
            onChange={setFrameSettings}
            currentImage={currentImage}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Apply frame
                applyImageFrame(frameSettings);
              }
              // Always close the panel
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      case 'logo':
        return (
          <LogoToolOptions 
            settings={logoSettings}
            onChange={setLogoSettings}
            currentImage={currentImage}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Apply logo
                applyImageLogo(logoSettings);
              }
              // Always close the panel
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      case 'magicErase': // Mask-based erase/inpainting
        return (
          <MagicEraseToolOptions
            settings={magicEraseSettings}
            onChange={setMagicEraseSettings}
            maskUrl={maskUrl}
            onApply={async (shouldApply) => {
              if (!shouldApply) {
                // Close the panel without applying
                setActiveTool(null);
                setOptionsPanelOpen(false);
                return;
              }
              
              if (!maskUrl) {
                showSnackbar('Lütfen önce bir maske oluşturun', 'warning');
                return;
              }
              
              // Get the user prompt from settings
              const userPrompt = magicEraseSettings.prompt;
              console.log("User prompt from settings:", userPrompt); // Log the raw prompt

              let translatedPrompt = ''; // Initialize translated prompt

              if (userPrompt) {
                // Always attempt translation if a user prompt exists
                console.log("Attempting translation for user prompt...");
                try {
                  setProcessing(true);
                  setProgress(10);
                  showSnackbar('İstek çeviriliyor...', 'info');

                  translatedPrompt = await translateTextWithGemini(userPrompt);
                  showSnackbar('İstek başarıyla çevrildi', 'success');
                  console.log("Translation successful:", translatedPrompt);

                } catch (error) {
                  console.error('Çeviri hatası:', error);
                  showSnackbar(`Çeviri başarısız oldu (${error.message}). Lütfen tekrar deneyin veya İngilizce yazın.`, 'error');
                  setProcessing(false);
                  setProgress(0);
                  return; // Stop if translation fails
                }
              } else {
                // No prompt provided, use default
                translatedPrompt = 'Remove the masked area and fill it naturally';
                console.log("No user prompt provided, using default:", translatedPrompt);
              }

              // --- Proceed Check ---
              // Ensure we have a valid prompt (either translated, original English-like, or default)
              if (!translatedPrompt) {
                 console.error("Error: translatedPrompt is empty after checks. Cannot proceed.");
                 showSnackbar("İstek işlenemedi. Lütfen tekrar deneyin.", "error");
                 setProcessing(false); // Ensure processing state is reset
                 setProgress(0);
                 return; // Stop execution
              }

              console.log("Proceeding with prompt for ComfyDeploy:", translatedPrompt);

              // Execute AI inpainting workflow
              try {
                executeAIWorkflow(
                  '716c3ff0-ada2-44c4-b4bd-7e576873ac1b', // ComfyDeploy Inpainting workflow ID
                  {
                    input_mask: maskUrl,
                    positive: translatedPrompt, // Use translated prompt for 'positive'
                    // original_prompt: userPrompt // Optional: Pass original for logging if needed
                  },
                  10 // Required credits
                );
              } catch (error) {
                 // Handle potential translation errors before calling executeAIWorkflow
                 console.error('Error during Magic Erase apply (translation/setup):', error);
                 showSnackbar(`İstek işlenemedi: ${error.message}`, 'error');
                 setProcessing(false);
                 setProgress(0);
              } finally {
                 // Ensure processing state is reset even if translation fails
                 // setProcessing(false); // executeAIWorkflow handles this
                 // setProgress(0);
              }
            }}
          />
        );

       case 'magicEdit': // Mask-free editing with AI suggestions
         return (
           <MagicEditToolOptions
             settings={magicEditSettings}
             onChange={setMagicEditSettings}
             currentImage={currentImage} // Pass current image for analysis
             onApply={async (shouldApply, finalPrompt) => {
               if (!shouldApply) {
                 setActiveTool(null);
                 setOptionsPanelOpen(false);
                 return;
               }

               if (!finalPrompt) {
                 showSnackbar('Lütfen bir düzenleme isteği girin', 'warning');
                 return;
               }

               console.log("Magic Edit applying with final prompt:", finalPrompt);

               try {
                 setProcessing(true); // Indicate processing starts
                 setProgress(5); // Small initial progress
                 showSnackbar('İstek çeviriliyor...', 'info');

                 // Translate the final prompt constructed by the component
                 const translatedPrompt = await translateTextWithGemini(finalPrompt);
                 showSnackbar('İstek başarıyla çevrildi', 'success');
                 console.log("Magic Edit translated prompt:", translatedPrompt);

                 if (!translatedPrompt) {
                   throw new Error("Çevrilen istek boş.");
                 }

                 // Execute the specific Gemini mask-free edit workflow
                 executeAIWorkflow(
                   'gemini-mask-free-edit', // Special identifier for the Gemini edit function
                   {
                     prompt: translatedPrompt, // Pass the translated prompt
                     original_prompt: finalPrompt // Keep original for reference
                   },
                   3 // Required credits for Magic Edit (Changed from 12)
                 );

               } catch (error) {
                 console.error('Error during Magic Edit apply (translation/setup):', error);
                 showSnackbar(`Düzenleme isteği işlenemedi: ${error.message}`, 'error');
                 setProcessing(false); // Reset processing on error
                 setProgress(0);
               }
             }}
           />
         );

      case 'virtualStaging': // Furnishing empty rooms
        return (
          <VirtualStagingToolOptions
            settings={virtualStagingSettings}
            onChange={setVirtualStagingSettings}
            onApply={(shouldApply, currentSettings) => { // Accept shouldApply and currentSettings
              if (shouldApply) {
                // Execute AI virtual staging workflow using the passed settings
                executeAIWorkflow(
                  'gemini-image-generation', // Use Gemini for fast staging
                  {
                    roomType: currentSettings.roomType,       // Use passed settings
                    style: currentSettings.style,           // Use passed settings
                    furnitureColor: currentSettings.colorTheme, // Use passed settings
                    // Construct a prompt suitable for Gemini image generation
                    prompt: `Furnish this empty ${currentSettings.roomType} with ${currentSettings.style} style furniture${currentSettings.colorTheme !== 'original' ? ` using predominantly ${currentSettings.colorTheme} colors` : ''}. Ensure the room looks realistic and well-lit.`,
                    // Pass other settings needed by the API function's options object
                    roomType: currentSettings.roomType,
                    style: currentSettings.style,
                    furnitureColor: currentSettings.colorTheme,
                    isRoomFurnished: false // Explicitly false for staging
                  },
                  8 // Required credits
                );
              }
              // Always close the panel after initiating or cancelling
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      case 'restyle':
        return (
          <RestyleToolOptions 
            settings={restyleSettings}
            onChange={setRestyleSettings}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Execute AI restyle workflow
                // Don't include reference image in the API call if it's not provided
                const apiParams = {
                    positive: `Refurnish this ${restyleSettings.roomType} in a ${restyleSettings.style} style${restyleSettings.colorTheme !== 'original' ? ` with a ${restyleSettings.colorTheme} color theme` : ''}.`,
                    // Add negative prompt if needed, e.g., negative: "ugly, messy, cluttered"
                    room_type: restyleSettings.roomType, // Pass parameters expected by Comfy workflow
                    style: restyleSettings.style,
                    color_theme: restyleSettings.colorTheme
                };

                // Only add reference image if it exists and is expected by the workflow
                if (restyleSettings.referenceImage) {
                  // Ensure the workflow input name matches (e.g., 'input_styleimage')
                  apiParams.input_styleimage = restyleSettings.referenceImage;
                }

                executeAIWorkflow(
                  '595026ca-47d7-4d5e-8906-a88a3b93a2c4', // ComfyDeploy Refurnish workflow ID
                  apiParams,
                  10 // Required credits
                );
              }
              // Always close the panel
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      case 'enhance':
        return (
          <EnhanceToolOptions 
            settings={enhanceSettings}
            onChange={setEnhanceSettings}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Execute AI enhance workflow with fixed settings
                executeAIWorkflow(
                  'f46b4d64-938b-407e-a28f-2f2f0e771501', // Enhance workflow ID
                  {
                    resolution: '8k', // Always use 8K for best results
                    sharpness: 0.75,  // Fixed optimal value (75%)
                    // Pass parameters expected by the Comfy workflow
                    resolution: '8k', // Fixed
                    sharpness: 0.75,  // Fixed
                    noise_reduction: 0.75 // Fixed
                  },
                  10 // Required credits
                );
              }
              // Always close the panel after initiating or cancelling
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      case 'video':
        return (
          <VideoToolOptions 
            settings={videoSettings}
            onChange={setVideoSettings}
            onApply={(shouldApply) => {
              if (shouldApply) {
                // Execute AI video generation workflow
                executeAIWorkflow(
                  'be11c67b-2585-4f58-925f-2cb1aa48b84a', // Video workflow ID
                  {
                    camera_movement: videoSettings.cameraMovement,
                    duration: videoSettings.duration,
                    quality: videoSettings.quality,
                    // Pass parameters expected by the Comfy workflow
                    camera_movement: videoSettings.cameraMovement,
                    duration: videoSettings.duration,
                    quality: videoSettings.quality,
                    mode: 'video' // Ensure mode is passed if workflow needs it
                  },
                  30 // Required credits
                );
              }
              // Always close the panel after initiating or cancelling
              setActiveTool(null);
              setOptionsPanelOpen(false);
            }}
          />
        );
        
      default:
        return null;
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <EditorContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2
        }}>
          <CircularProgress />
          <Typography>Görsel yükleniyor...</Typography>
        </Box>
      </EditorContainer>
    );
  }
  
  return (
    <EditorContainer>
      {/* Top Bar */}
      <TopBar>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, sm: 2 },
          minWidth: { xs: 'auto', sm: '200px' }
        }}>
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{ padding: { xs: '4px', sm: '8px' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {imageData?.prompt?.substring(0, 30) || 'Görsel Düzenleyici'}
            {imageData?.prompt?.length > 30 ? '...' : ''}
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 },
          overflowX: 'auto',
          flexWrap: 'nowrap',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}>
          <Tooltip title="Geri Al">
            <span>
              <IconButton 
                onClick={handleUndo} 
                disabled={historyIndex <= 0 || processing}
                size="small"
                sx={{ padding: { xs: '4px', sm: '8px' } }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Yinele">
            <span>
              <IconButton 
                onClick={handleRedo} 
                disabled={historyIndex >= history.length - 1 || processing}
                size="small"
                sx={{ padding: { xs: '4px', sm: '8px' } }}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            border: theme => `1px solid ${theme.palette.divider}`,
            borderRadius: '16px',
            padding: '0 4px',
            mx: { xs: 0.5, sm: 1 },
            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
          }}>
            <Tooltip title="Uzaklaştır">
              <IconButton 
                onClick={() => handleZoom('out')} 
                disabled={processing}
                size="small"
                sx={{ padding: { xs: '2px', sm: '4px' } }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Typography 
              variant="caption" 
              sx={{ 
                mx: { xs: 0.5, sm: 1 },
                minWidth: '36px',
                textAlign: 'center'
              }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            
            <Tooltip title="Yakınlaştır">
              <IconButton 
                onClick={() => handleZoom('in')} 
                disabled={processing}
                size="small"
                sx={{ padding: { xs: '2px', sm: '4px' } }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Tooltip title="Pozisyonu Sıfırla">
            <span>
              <IconButton 
                onClick={() => setPosition({ x: 0, y: 0 })}
                disabled={processing || (position.x === 0 && position.y === 0)}
                size="small"
                sx={{ 
                  padding: { xs: '4px', sm: '8px' },
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                <CenterFocusWeakIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Kaydet">
            <IconButton 
              onClick={handleSaveMenuOpen}
              disabled={processing}
              size="small"
              sx={{ padding: { xs: '4px', sm: '8px' } }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Yardım">
            <IconButton
              size="small"
              sx={{ 
                padding: { xs: '4px', sm: '8px' },
                display: { xs: 'none', sm: 'flex' }
              }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {/* More menu button (visible only on mobile) */}
          <Tooltip title="Daha Fazla">
            <IconButton
              onClick={handleMoreMenuOpen}
              size="small"
              sx={{ 
                padding: { xs: '4px', sm: '8px' },
                display: { xs: 'flex', sm: 'none' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TopBar>
      
      {/* Tool Bar */}
      <ToolBar>
        {/* Basic Editing Tools */}
        <ToolGroup>
          <ToolGroupLabel>Temel</ToolGroupLabel>
          
          <Tooltip title="Kırp">
            <ToolButton 
              active={activeTool === 'crop'}
              onClick={() => handleToolSelect('crop')}
              disabled={processing}
              startIcon={<CropIcon />}
            >
              Kırp
            </ToolButton>
          </Tooltip>
          
          <Tooltip title="Ayarla">
            <ToolButton 
              active={activeTool === 'adjust'}
              onClick={() => handleToolSelect('adjust')}
              disabled={processing}
              startIcon={<TuneIcon />}
            >
              Ayarla
            </ToolButton>
          </Tooltip>
          
          {/* <Tooltip title="Not Ekle">
            <ToolButton 
              active={activeTool === 'annotate'}
              onClick={() => handleToolSelect('annotate')}
              disabled={processing}
              startIcon={<TextFieldsIcon />}
            >
              Not Ekle
            </ToolButton>
          </Tooltip> */}
          
          <Tooltip title="Çerçevele">
            <ToolButton 
              active={activeTool === 'frame'}
              onClick={() => handleToolSelect('frame')}
              disabled={processing}
              startIcon={<BorderStyleIcon />}
            >
              Çerçevele
            </ToolButton>
          </Tooltip>
          
          <Tooltip title="Logo Ekle">
            <ToolButton 
              active={activeTool === 'logo'}
              onClick={() => handleToolSelect('logo')}
              disabled={processing}
              startIcon={<BrandingWatermarkIcon />}
            >
              Logo
            </ToolButton>
          </Tooltip>
        </ToolGroup>
        
        {/* AI Tools */}
        <ToolGroup>
          <ToolGroupLabel>AI Araçları</ToolGroupLabel>
          
          <Tooltip title="Sihirli Değnek (Maske ile)">
            <ToolButton
              active={activeTool === 'magicErase'}
              onClick={() => handleToolSelect('magicErase')}
              disabled={true} // Permanently disable this button
              startIcon={<AutoFixHighIcon />}
            >
              Sihirli Değnek
            </ToolButton>
          </Tooltip>

          <Tooltip title="Sihirli Düzenleme (Maskesiz)">
            <ToolButton
              active={activeTool === 'magicEdit'}
              onClick={() => handleToolSelect('magicEdit')}
              disabled={processing}
              startIcon={<AutoFixNormalIcon />}
            >
              Sihirli Düzenleme
            </ToolButton>
          </Tooltip>
          
          <Tooltip title="Sanal Dizayn">
            <ToolButton
              active={activeTool === 'virtualStaging'}
              onClick={() => handleToolSelect('virtualStaging')}
              disabled={processing}
              startIcon={<WeekendIcon />}
            >
              Sanal Dizayn
            </ToolButton>
          </Tooltip>
          
          <Tooltip title="Yeniden Dekore Et">
            <ToolButton 
              active={activeTool === 'restyle'}
              onClick={() => handleToolSelect('restyle')}
              disabled={processing}
              startIcon={<StyleIcon />}
            >
              Yeniden Dekore
            </ToolButton>
          </Tooltip>
          
          <Tooltip title="İyileştir">
            <ToolButton 
              active={activeTool === 'enhance'}
              onClick={() => handleToolSelect('enhance')}
              disabled={processing}
              startIcon={<HdIcon />}
            >
              İyileştir
            </ToolButton>
          </Tooltip>
          
          {/* Video tool hidden */}
        </ToolGroup>
      </ToolBar>
      
      <Box sx={{ 
        flex: 1, 
        position: 'relative', 
        padding: 0, 
        margin: 0,
        width: '100%',
        maxWidth: '100vw',
        paddingTop: { xs: '128px', sm: 0 } // Add padding on mobile to account for fixed toolbars
      }}>
        
        {/* Canvas Area */}
        <CanvasArea className="canvas-area">
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                position: 'relative',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {currentImage && (
                <img
                  src={currentImage}
                  alt="Editor"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
                    transition: 'transform 0.3s ease',
                    cursor: isDragging ? 'grabbing' : 'grab'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  draggable="false"
                />
              )}
              
              <canvas
                ref={canvasRef}
                style={{ display: 'none' }} // Hidden canvas for processing
              />
              
              {processing && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000 // Increased z-index to ensure it's visible above other elements
                  }}
                >
                  <CircularProgress sx={{ color: 'white', mb: 2 }} />
                  <Typography sx={{ color: 'white', mb: 2, textAlign: 'center', px: 2 }}>
                    İşleniyor... {Math.round(progress)}%
                  </Typography>
                  <Box sx={{ 
                    width: '80%', 
                    maxWidth: 400,
                    position: 'relative',
                    zIndex: 1001 // Ensure progress bar is visible
                  }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Masking Canvas (positioned absolutely) */}
          {isMaskingEnabled && (
            <Box sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%', 
              zIndex: 1400 
            }}>
              <MaskingCanvas
                image={currentImage}
                onClose={() => setIsMaskingEnabled(false)}
                onSaveMask={handleSaveMask}
              />
            </Box>
          )}
          
          {/* Floating Options Panel */}
          {optionsPanelOpen && (
            <OptionsPanel open={optionsPanelOpen}>
              {renderOptionsPanel()}
            </OptionsPanel>
          )}
        </CanvasArea>
      </Box>
      
      {/* Save Menu */}
      <Menu
        anchorEl={saveMenuAnchorEl}
        open={Boolean(saveMenuAnchorEl)}
        onClose={handleSaveMenuClose}
      >
        <MenuItem onClick={handleSave}>
          <SaveIcon sx={{ mr: 1 }} />
          Kaydet
        </MenuItem>
        <MenuItem onClick={handleSaveAs}>
          <ContentCopyIcon sx={{ mr: 1 }} />
          Kopya Olarak Kaydet
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <FileDownloadIcon sx={{ mr: 1 }} />
          İndir
        </MenuItem>
      </Menu>
      
      {/* More Options Menu (for mobile) */}
      <Menu
        anchorEl={moreMenuAnchorEl}
        open={Boolean(moreMenuAnchorEl)}
        onClose={handleMoreMenuClose}
      >
        <MenuItem onClick={() => {
          setPosition({ x: 0, y: 0 });
          handleMoreMenuClose();
        }}>
          <CenterFocusWeakIcon sx={{ mr: 1 }} />
          Pozisyonu Sıfırla
        </MenuItem>
        <MenuItem onClick={handleMoreMenuClose}>
          <HelpOutlineIcon sx={{ mr: 1 }} />
          Yardım
        </MenuItem>
      </Menu>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
      >
        <DialogTitle>{confirmDialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose}>İptal</Button>
          <Button 
            onClick={handleConfirmAction} 
            variant="contained" 
            color="primary"
            autoFocus
          >
            Devam Et
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Credit Alert */}
      <CreditAlert
        open={showCreditAlert}
        onClose={() => setShowCreditAlert(false)}
        requiredCredits={creditAlertData.required}
        availableCredits={creditAlertData.available}
      />
      
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </EditorContainer>
  );
};

export default ImageEditor;
